import React from 'react';
import { Grid } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import { UserEntity } from 'ha/types/UserEntity';
import { getLastActivityHumanized } from 'ha/utils/getLastActivityHumanized';
import * as Section from 'ha/ui.legacy/Section';
import { LongParagraph } from 'ha/ui.legacy/LongParagraph';
import { Typography, units } from '@hbf/dsl';

import { UserCardCountryOrLanguage } from './UserCardCountryOrLanguage';
import { UserCardProfileAvatar } from './UserCardProfileAvatar';
import { UserCardProfileName } from './UserCardProfileName';
import { UserCardVerifiedBadge } from './UserCardVerifiedBadge';
import { ExcellentAdvertiserBadge } from '../ExcellentAdvertiserBadge/ExcellentAdvertiserBadge';

const useStyles = makeStyles()(theme => ({
  avatar: {
    position: 'relative',
    textAlign: 'center',
  },
  userCard: {
    position: 'relative',
    textAlign: 'left',
    paddingBottom: 0,
  },
  avatarRedesign: {
    marginBottom: units.rem(1),
  },
  info: {
    paddingTop: units.rem(1),
    paddingLeft: units.rem(0.5),
  },
  location: {
    fontSize: units.rem(0.8),
  },
  badge: {
    zIndex: 2,
    marginTop: theme.spacing(2),
  },
  badgeRedesign: {
    zIndex: 2,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
}));

interface Props {
  user: UserEntity;
  linkToProfile?: boolean;
  showDescription?: boolean;
  newAdvertiserDesign?: boolean;
}

const UserCardProfile: React.FC<Props> = ({
  user,
  linkToProfile,
  showDescription = true,
  newAdvertiserDesign = false,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();

  if (newAdvertiserDesign) {
    return (
      <section>
        <Section.Text
          className={classes.userCard}
          data-test-locator="User Card Avatar Info"
        >
          <Grid container wrap="nowrap" className={classes.avatarRedesign}>
            <UserCardProfileAvatar
              user={user}
              linkToProfile={linkToProfile}
              newAdvertiserDesign
            />
            <div className={classes.info}>
              <Typography.H4>{user.firstName}</Typography.H4>
              <Typography.Caption className={classes.location}>
                {T(user.location)}
              </Typography.Caption>
            </div>
          </Grid>
          {user.isTrusted && (
            <div className={classes.badgeRedesign}>
              <ExcellentAdvertiserBadge newAdvertiserDesign />
            </div>
          )}
        </Section.Text>
      </section>
    );
  }

  return (
    <section>
      <Section.Text
        className={classes.avatar}
        centered
        data-test-locator="User Card Avatar Info"
      >
        <UserCardProfileAvatar user={user} linkToProfile={linkToProfile} />
        {user.isVerifiedTenant && !user.isTrusted && <UserCardVerifiedBadge />}
        {user.isTrusted && (
          <div className={classes.badge}>
            <ExcellentAdvertiserBadge />
          </div>
        )}
      </Section.Text>
      <Section.Text centered>
        <UserCardProfileName user={user} linkToProfile />
        <UserCardCountryOrLanguage
          variant="paragraph"
          spokenLanguages={user.spokenLanguages}
          location={user.location}
        />
        {user.lastActivityAt ? (
          <Typography.HelperText
            color="mint"
            data-test-locator="User Card Lastseen"
          >
            {getLastActivityHumanized(T, user.lastActivityAt)}
          </Typography.HelperText>
        ) : null}
      </Section.Text>
      {showDescription && user.description ? (
        <Section.Text centered>
          <LongParagraph>{user.description}</LongParagraph>
        </Section.Text>
      ) : null}
    </section>
  );
};

export { UserCardProfile };
